import { MessageOutlined } from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import { Restricted } from '@procureai/permissions-ui';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RouteId } from '../RouteId';
import { useFrontdeskAssistant } from '@/legacy/hooks/useFrontdeskAssistant/useFrontdeskAssistant';
import { I18nNamespace, FeatureFlag } from '~/common/types';
import { Disclaimer } from '~/components/ConversationInput/components/Disclaimer/Disclaimer';
import { ConversationInput } from '~/components/ConversationInput/ConversationInput';
import { ConversationLayout } from '~/components/ConversationLayout/ConversationLayout';
import { conversationLayoutClasses } from '~/components/ConversationLayout/ConversationLayout.styled';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { pageLayoutClasses } from '~/components/PageLayout/PageLayout.styled';
import { useRuntime } from '~/components/RuntimeProvider';

import { ToolSelection } from './components/ToolSelection/ToolSelection';
import { useIntakeCreate } from './IntakeCreate.hook';

const StyledPageLayout = styled(PageLayout)(() => ({
  maxHeight: '100vh', // enforce max height to avoid page scrolling
  [`& > .${pageLayoutClasses.content}.${pageLayoutClasses.fullHeight}`]: {
    minHeight: 0, // min-height: 0 enforces flex grow behavior
  },
}));

export const IntakeCreate = () => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeCreate' });
  const { t: tChat } = useTranslation(I18nNamespace.intake, { keyPrefix: 'Chat' });
  const agentId = useFrontdeskAssistant();

  const navigate = useNavigate();
  const threadCallback = (threadId: string, initialMessage?: string) =>
    navigate(RouteId.intakeChat(threadId), {
      state: { initialMessage },
    });
  const formCallback = (chatSessionId: string) => navigate(RouteId.intakeDetail(chatSessionId));

  const { onClick, onClickNewThread, isCreatingThread } = useIntakeCreate({
    formCallback,
    threadCallback,
  });

  const { runtime } = useRuntime();

  return (
    <StyledPageLayout
      fullHeight
      disablePadding
      renderHeader={
        runtime?.type !== 'browserExtension' ? (
          <PageHeaderStatic
            maxWidth={false}
            // avoid header shrinking in flex layout, should stay at specified height
            sx={{ flexShrink: 0 }}
            icon={<WidgetIcon color="primary" icon={<MessageOutlined fontSize="small" />} />}
            title={t('title')}
          />
        ) : undefined
      }
    >
      <ConversationLayout
        sx={{
          [` & .${conversationLayoutClasses.inputContainer} > *`]: {
            maxWidth: {
              xl: 1280,
            },
          },
          [`& .${conversationLayoutClasses.inputContainer}`]: {
            background: `linear-gradient(0deg, #FAFAFA, 95%, transparent)`,
          },
        }}
        input={
          <Restricted needs={{ feature: FeatureFlag.SHOW_AGENT_FRONTDESK }}>
            <ConversationInput
              placeholder={tChat('input.placeholder')}
              isLoading={isCreatingThread}
              disabled={isCreatingThread}
              onSubmit={(args) => {
                // create a new thread with the general frontdesk agent
                // NOTE: we currently do not pass files yet (from the initial message)
                onClickNewThread(agentId, args.text);
              }}
            />
          </Restricted>
        }
        disclaimer={<Disclaimer />}
      >
        <Typography variant="h4" align="center" marginTop={5} marginBottom={7} display={{ xs: 'none', md: 'block' }}>
          {t('message')}
        </Typography>

        <ToolSelection onClick={onClick} onClickNewThread={onClickNewThread} />
      </ConversationLayout>
    </StyledPageLayout>
  );
};
